import React, { useRef, useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
	type GraphQLTaggedNode,
	graphql,
	useLazyLoadQuery,
	usePaginationFragment,
} from 'react-relay';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	SCREEN,
	FireScreenAnalytics,
	FireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import type { aquaOutgoingEmailLogsQueryWithFilters } from '@atlassian/jira-relay/src/__generated__/aquaOutgoingEmailLogsQueryWithFilters.graphql';
import type { notificationLogsPage_spaAppsServicedeskNotificationLogs_NotificationLogsWithFilters$key as notificationLogsFragment } from '@atlassian/jira-relay/src/__generated__/notificationLogsPage_spaAppsServicedeskNotificationLogs_NotificationLogsWithFilters.graphql';
import type {
	notificationLogsPageWithFiltersQuery,
	notificationLogsPageWithFiltersQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/notificationLogsPageWithFiltersQuery.graphql';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { ProjectNotFound } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/errors/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useOutgoingMailConfig } from '../../services/outgoing-mail-config';
import { EnableOutgoingMailInfo } from './enable-outgoing-mail-info';
import { LogsTable } from './logs-table';
import { messages } from './messages';
import { NotificationAppSubHeader } from './notification-app-sub-header';
import { NotificationLogsTabLayout } from './notification-logs-tab-layout';

const GetNotificationLogsBreadcrumbs = (
	projectContext: ProjectContext | null,
	breadcrumbRoot: string,
) => (
	<Box xcss={breadcrumbContainerStyles}>
		{/* Replace with lodash/noop */}
		{/* eslint-disable-next-line @typescript-eslint/no-empty-function */}
		<Breadcrumbs onExpand={() => {}}>
			<BreadcrumbsItem text={breadcrumbRoot} href="/jira/projects" />
			<BreadcrumbsItem
				text={`${projectContext?.projectName}`}
				href={`/browse/${projectContext?.projectKey}`}
			/>
		</Breadcrumbs>
	</Box>
);

export const QUERY_WITH_FILTERS = graphql`
	query notificationLogsPageWithFiltersQuery(
		$cloudId: ID!
		$projectId: Long
		$notificationType: String
		$notificationActionType: String
	) {
		...notificationLogsPage_spaAppsServicedeskNotificationLogs_NotificationLogsWithFilters
	}
`;

const PAZE_SIZE = 30;
const NO_ERROR = 'No Error';

export const NotificationLogs = ({ projectContext }: { projectContext: ProjectContext }) => {
	const { formatMessage } = useIntl();

	const outgoingMailConfigData = useOutgoingMailConfig();
	const { outgoingMailConfig, error, loading } = outgoingMailConfigData;

	if (error) {
		fireErrorAnalytics({
			meta: {
				id: 'ErrorFetchingOutgoingMailConfigData',
				packageName: 'jiraSpaAppsServicedeskNotificationLogs',
				teamName: 'spartans',
			},
			error: new Error(error),
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	const cloudId = useCloudId();
	const projectId = projectContext?.projectId;
	const projectKey = projectContext?.projectKey;

	const breadcrumbRoot = formatMessage(messages.projects);
	const breadcrumbs = GetNotificationLogsBreadcrumbs(projectContext, breadcrumbRoot);

	const LOGS_QUERY: GraphQLTaggedNode = QUERY_WITH_FILTERS;
	const notificationActionType = 'CUSTOMER_INVITE';
	const notificationType = null;
	const variables: notificationLogsPageWithFiltersQuery$variables = {
		cloudId,
		projectId,
		notificationActionType,
		notificationType,
	};
	const graphQLNode: GraphQLTaggedNode = graphql`
		fragment notificationLogsPage_spaAppsServicedeskNotificationLogs_NotificationLogsWithFilters on Query
		@refetchable(queryName: "aquaOutgoingEmailLogsQueryWithFilters")
		@argumentDefinitions(count: { type: "Int", defaultValue: 20 }, cursor: { type: "String" }) {
			aquaOutgoingEmailLogs(cloudId: $cloudId) {
				GetNotificationLogs(
					projectId: $projectId
					notificationType: $notificationType
					notificationActionType: $notificationActionType
				) {
					... on AquaOutgoingEmailLog {
						logs(first: $count, after: $cursor) @connection(key: "aquaOutgoingEmail__logs") {
							edges {
								...logsTable_logEdges
							}
							pageInfo {
								startCursor
								endCursor
								hasNextPage
								hasPreviousPage
							}
						}
					}
					... on QueryError {
						__typename
						identifier
						message
					}
				}
			}
		}
	`;

	const emailData = useLazyLoadQuery<notificationLogsPageWithFiltersQuery>(LOGS_QUERY, variables);

	const { data, loadNext } = usePaginationFragment<
		aquaOutgoingEmailLogsQueryWithFilters,
		notificationLogsFragment
	>(graphQLNode, emailData);

	const responseType = data.aquaOutgoingEmailLogs?.GetNotificationLogs?.__typename;

	if (responseType === 'QueryError') {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const errorMessage: any = data.aquaOutgoingEmailLogs?.GetNotificationLogs?.message;
		const queryError: Error = new Error(errorMessage);
		fireErrorAnalytics({
			meta: {
				id: 'LogsQueryReturnedError',
				packageName: 'jiraSpaAppsServicedeskNotificationLogs',
				teamName: 'spartans',
			},
			error: queryError,
			sendToPrivacyUnsafeSplunk: true,
		});
		throw queryError;
	}

	const hasMoreData = data.aquaOutgoingEmailLogs?.GetNotificationLogs?.logs?.pageInfo.hasNextPage;

	const allRows = data;
	const allRowEdges = allRows.aquaOutgoingEmailLogs?.GetNotificationLogs?.logs?.edges;

	const hasError = allRows.aquaOutgoingEmailLogs?.GetNotificationLogs?.identifier
		? allRows.aquaOutgoingEmailLogs?.GetNotificationLogs.message
		: NO_ERROR;

	const parentRef = useRef<HTMLDivElement>(null);
	const getRowHeight = useCallback(() => 60, []);
	const allRowsLength = allRowEdges ? allRowEdges.length : 0;

	const virtualizer = useVirtualizer({
		count: hasMoreData ? allRowsLength + 1 : allRowsLength,
		getScrollElement: () => parentRef.current,
		estimateSize: getRowHeight,
		overscan: 5,
	});

	useEffect(() => {
		// save calling `getVirtualItems()` unnecessarily
		if (!allRowEdges || !hasMoreData) {
			return;
		}

		const lastItem = virtualizer.getVirtualItems().at(-1);

		if (lastItem && lastItem.index >= allRowEdges.length - 1) {
			loadNext(PAZE_SIZE);
		}
		// Do not change the dependency to just be `virtualizer` or `virtualizer.getVirtualItems`
		// it must call the function for dependencies to be calculated correctly (and therefore render at all).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasMoreData, loadNext, virtualizer.getVirtualItems(), allRowEdges]);

	return (
		<JSErrorBoundary
			id="CustomerNotificationLogs"
			packageName="jiraSpaAppsServicedeskNotificationLogs"
			teamName="spartans"
		>
			<ContextualAnalyticsData sourceName="customerNotificationLogs" sourceType={SCREEN}>
				<Box xcss={notificationAppStyles}>
					<PageHeader breadcrumbs={breadcrumbs}>{formatMessage(messages.title)}</PageHeader>

					<>
						<NotificationAppSubHeader />
						{outgoingMailConfig !== null && !error && !loading && !outgoingMailConfig?.enabled && (
							<>
								<FireTrackAnalytics eventName="LogsTable OutGoingMailDisabledText" />
								<EnableOutgoingMailInfo />
							</>
						)}
					</>

					<LogsTableWrapper ref={parentRef}>
						{hasError === NO_ERROR && (
							<>
								<FireTrackAnalytics eventName="LogsTable Render" />
								<LogsTable
									virtualizer={virtualizer}
									// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
									allEdges={allRowEdges || ([] as any)}
									projectKey={projectKey}
									outgoingMailConfigData={outgoingMailConfigData}
								/>
							</>
						)}
					</LogsTableWrapper>
				</Box>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</JSErrorBoundary>
	);
};

export const NotificationLogsPage = () => {
	const { data: projectContext } = useProjectContext();
	if (!projectContext) {
		fireErrorAnalytics({
			meta: {
				id: 'ProjectContextNULL',
				packageName: 'jiraSpaAppsServicedeskNotificationLogs',
				teamName: 'spartans',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
		return null;
	}
	const isProjectAdmin = Boolean(projectContext?.isProjectAdmin);
	if (!isProjectAdmin) {
		fireErrorAnalytics({
			meta: {
				id: 'UserIsNotProjectAdmin',
				packageName: 'jiraSpaAppsServicedeskNotificationLogs',
				teamName: 'spartans',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
		return (
			<div data-testid="spa-apps-servicedesk-notification-logs.ui.notification-logs-page.project-not-found">
				<ProjectNotFound />
			</div>
		);
	}
	if (ff('sptn-1793-cn-logs-for-request-notifications_a9umh')) {
		return <NotificationLogsTabLayout projectContext={projectContext} />;
	}
	return <NotificationLogs projectContext={projectContext} />;
};

const breadcrumbContainerStyles = xcss({
	marginLeft: 'space.negative.050',
});

const notificationAppStyles = xcss({
	marginLeft: 'space.500',
	marginRight: 'space.500',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LogsTableWrapper = styled.div({
	height: '75vh',
	width: '100%',
	overflow: 'auto',
	marginTop: token('space.200', '16px'),
});
